import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Form, Input, Button, Typography, ConfigProvider, theme, Alert } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import apiService from '../../services/api';

const { Title, Text } = Typography;

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const onFinish = async ({ password }) => {
    setError('');
    setLoading(true);
    try {
      await apiService.auth.confirmResetPassword(token, password);
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Reset failed. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ConfigProvider theme={{ algorithm: theme.defaultAlgorithm, token: { colorPrimary: '#1677ff', borderRadius: 10 } }}>
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        background: 'linear-gradient(135deg, #e8edf5 0%, #f0f4fb 50%, #e4eaf4 100%)',
      }}>
        <div style={{
          width: '100%',
          maxWidth: 420,
          background: '#ffffff',
          borderRadius: 20,
          overflow: 'hidden',
          boxShadow: '0 4px 6px rgba(0,0,0,0.04), 0 20px 60px rgba(0,0,0,0.08)',
          border: '1px solid rgba(0,0,0,0.06)',
        }}>
          <div style={{ height: 4, background: 'linear-gradient(90deg, #6330f5 0%, #1677ff 50%, #00b4d8 100%)' }} />
          <div style={{ padding: '36px 36px 32px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 28 }}>
              <div style={{
                width: 38, height: 38, borderRadius: 10,
                background: 'linear-gradient(135deg, #1677ff 0%, #6330f5 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 800, fontSize: 14, color: '#fff',
              }}>LT</div>
              <span style={{ fontWeight: 700, fontSize: 15, color: '#1a1a2e' }}>LITE Transport</span>
            </div>

            {done ? (
              <div style={{ textAlign: 'center', padding: '16px 0' }}>
                <div style={{ fontSize: 40, marginBottom: 16 }}>✓</div>
                <Title level={4} style={{ margin: '0 0 8px' }}>Password reset!</Title>
                <Text style={{ color: '#64748b' }}>Your password has been updated successfully.</Text>
                <Button
                  type="primary"
                  block
                  size="large"
                  onClick={() => navigate('/login')}
                  style={{ marginTop: 24, height: 46, borderRadius: 10, fontWeight: 600, background: 'linear-gradient(135deg, #1677ff 0%, #6330f5 100%)', border: 'none' }}
                >
                  Go to login
                </Button>
              </div>
            ) : (
              <>
                <div style={{ marginBottom: 28 }}>
                  <Title level={3} style={{ margin: '0 0 4px', fontWeight: 700, color: '#0f0f1a' }}>Set new password</Title>
                  <Text style={{ color: '#94a3b8', fontSize: 14 }}>Choose a strong password for your account</Text>
                </div>

                {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 20, borderRadius: 8 }} />}

                <Form onFinish={onFinish} layout="vertical">
                  <Form.Item
                    name="password"
                    label={<span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>New password</span>}
                    rules={[
                      { required: true, message: 'Please enter a new password' },
                      { min: 6, message: 'Password must be at least 6 characters' },
                    ]}
                    style={{ marginBottom: 16 }}
                  >
                    <Input.Password
                      size="large"
                      prefix={<LockOutlined style={{ color: '#c0c8d8', fontSize: 14 }} />}
                      placeholder="••••••••"
                      style={{ borderColor: '#e2e8f0', borderRadius: 10, height: 46 }}
                    />
                  </Form.Item>

                  <Form.Item
                    name="confirm"
                    label={<span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Confirm password</span>}
                    dependencies={['password']}
                    rules={[
                      { required: true, message: 'Please confirm your password' },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue('password') === value) return Promise.resolve();
                          return Promise.reject(new Error('Passwords do not match'));
                        },
                      }),
                    ]}
                    style={{ marginBottom: 24 }}
                  >
                    <Input.Password
                      size="large"
                      prefix={<LockOutlined style={{ color: '#c0c8d8', fontSize: 14 }} />}
                      placeholder="••••••••"
                      style={{ borderColor: '#e2e8f0', borderRadius: 10, height: 46 }}
                    />
                  </Form.Item>

                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    block
                    size="large"
                    style={{
                      height: 46, borderRadius: 10, fontWeight: 600, fontSize: 15,
                      background: 'linear-gradient(135deg, #1677ff 0%, #6330f5 100%)',
                      border: 'none',
                    }}
                  >
                    Reset password
                  </Button>
                </Form>

                <div style={{ textAlign: 'center', marginTop: 20 }}>
                  <Link to="/login" style={{ fontSize: 13, color: '#94a3b8' }}>Back to login</Link>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </ConfigProvider>
  );
};

export default ResetPassword;
